import { useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import useMeasure from 'react-use-measure';

import { Box } from '@frontend/components';

import { useDrop } from 'components/customDragLayer';
import {
  CustomDragLayer,
  CUSTOM_DRAGGABLE_ITEM_TYPES,
} from 'components/customDragLayer';

import { ScrollRootHeightContextProvider } from 'containers/Layouts/DashboardLayout/ScrollRootHeightContext';

import {
  isStripeAccountCompliant as getIsStripeAccountCompliant,
  hasStripeAccount as getHasStripeAccount,
  hasCreditCardCheckoutEnabled as getHasCreditCardCheckoutEnabled,
  getHasCustomerSupportRecord,
} from 'store/session/selectors';

import Header from './Header';
import { Nav } from './Nav';
import MobileTabs from './components/MobileTabs';
import StripeBanner from './components/StripeBanner';
import { UserTimeoutWarning } from './components/UserTimeoutWarning';

export type DashboardLayoutProps = PropsWithChildren<unknown>;

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const hasCustomerSupportRecord = useSelector(getHasCustomerSupportRecord);
  const hasStripeAccount = useSelector(getHasStripeAccount);
  const hasCreditCardCheckoutEnabled = useSelector(
    getHasCreditCardCheckoutEnabled,
  );
  const isStripeAccountCompliant = useSelector(getIsStripeAccountCompliant);
  const [scrollRootRef, scrollRootBounds] = useMeasure();
  const [timeoutAlertRef, timeoutAlertMeasure] = useMeasure();

  const scrollRootHeight = useMemo(
    () => scrollRootBounds.height,
    [scrollRootBounds.height],
  );

  // the following is a hack to prevent a delay after an unsuccessful drop (see https://github.com/react-dnd/react-dnd/issues/3115#issuecomment-826799284)
  const [, dropRef] = useDrop({
    accept: Object.values(CUSTOM_DRAGGABLE_ITEM_TYPES),
  });

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        width="100vw"
        maxWidth="100vw"
        height="100%"
        overflow="hidden"
        position="fixed"
        top="0"
        left="0"
        ref={dropRef}
        pt={`${timeoutAlertMeasure.height}px`}
      >
        <Header>
          {hasCustomerSupportRecord &&
            hasCreditCardCheckoutEnabled &&
            hasStripeAccount &&
            !isStripeAccountCompliant && <StripeBanner />}
          <UserTimeoutWarning
            ref={timeoutAlertRef}
            zIndex={timeoutAlertMeasure.height === 0 ? -1 : 'toast'}
          />
          <Nav />
        </Header>
        <ScrollRootHeightContextProvider value={scrollRootHeight}>
          <Box
            ref={scrollRootRef}
            overflowY="auto"
            height="100%"
            flexGrow={1}
            id="scrollRoot"
          >
            {children}
          </Box>
        </ScrollRootHeightContextProvider>
        <MobileTabs />
      </Box>
      <CustomDragLayer />
    </>
  );
};

export default DashboardLayout;
